/**
 * API Service for T-RaX Car Care
 * 
 * This service handles all backend API calls for payments.
 * Orders are stored in localStorage and managed by adminStore.
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export interface OrderItem {
  productId?: number;
  name: string;
  quantity: number;
  unitPrice: number;
}

export interface CreateOrderData {
  orderId: string;
  amount: number;
  currency: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  shippingAddress: string;
  billingAddress: string;
  city?: string;
  district?: string;
  postalCode?: string;
  items: OrderItem[];
  notes?: string;
  couponCode?: string;
  couponDiscount?: number;
  csrfToken?: string;
}

export interface CreatePaymentResponse {
  success: boolean;
  token?: string;
  iframe_url?: string;
  order_id?: string;
  order_number?: string;
  error?: string;
  test_mode?: boolean;
}

class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  /**
   * Create a new order and initiate Paytr payment
   */
  async createPayment(data: CreateOrderData): Promise<CreatePaymentResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/paytr/create-payment.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Ödeme başlatılamadı');
      }

      return await response.json();
    } catch (error) {
      console.error('Create payment error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Bilinmeyen hata',
      };
    }
  }

  /**
   * Get all orders from backend (for admin panel)
   */
  async getOrders(): Promise<{ success: boolean; orders: any[]; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/orders/list.php`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Siparişler alınamadı');
      }

      return await response.json();
    } catch (error) {
      console.error('Get orders error:', error);
      return {
        success: false,
        orders: [],
        error: error instanceof Error ? error.message : 'Bilinmeyen hata',
      };
    }
  }

  /**
   * Get a single order by order number (used on the success/fail return pages)
   */
  async getOrder(orderNumber: string): Promise<{ success: boolean; order?: any; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/orders/get.php?orderNumber=${encodeURIComponent(orderNumber)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Sipariş bulunamadı');
      }

      return await response.json();
    } catch (error) {
      console.error('Get order error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Bilinmeyen hata',
      };
    }
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; timestamp: string; test_mode: boolean }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return await response.json();
    } catch (error) {
      console.error('Health check error:', error);
      return {
        status: 'error',
        timestamp: new Date().toISOString(),
        test_mode: false,
      };
    }
  }
}

export const apiService = new ApiService();
export default apiService;
